import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { Candidate, Company, Job } from '@shared';
import { ALL_JOB_STATUS } from '@shared/models/job-statuses';
import { Pagination } from '@shared/pagination';
import { CandidateService, CompanyService, JobService } from '@shared/services';

import * as LayoutActions from './layout.actions';
import { Store } from '@ngrx/store';
import { hasAnonymization } from '@collections/users';

export const DESKTOP_LIST_SIZE = 6;
export const MOBILE_LIST_SIZE = 3;

@Injectable()
export class LayoutEffects {
  loadGlobalSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.layoutHeaderLoadGlobalSearch),
      withLatestFrom(this.store.select(hasAnonymization)),
      switchMap(([{ search, apiParams }, hasAnonymization]) =>
        forkJoin([
          this.candidateService.searchCandidates(
            search,
            { size: apiParams.size },
            hasAnonymization,
          ),
          this.jobService.searchJobs({ name: search, size: apiParams.size }, ALL_JOB_STATUS),
          this.companyService
            .getFilterCompanies(search, apiParams.size)
            .pipe(map((pagination) => pagination.content)),
        ]).pipe(
          map(([candidates, jobsResult, companies]: [Candidate[], Pagination<Job>, Company[]]) =>
            LayoutActions.layoutHeaderLoadGlobalSearchSuccess({
              candidates,
              jobs: jobsResult.content,
              companies,
            }),
          ),
          catchError((error) => of(LayoutActions.layoutHeaderLoadGlobalSearchFail({ error }))),
        ),
      ),
    ),
  );

  cacheGlobalSearchResultsInCandidatesCollections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LayoutActions.layoutHeaderLoadGlobalSearchSuccess),
      mergeMap(({ candidates, jobs, companies }) => [
        LayoutActions.layoutHeaderLoadCandidatesSuccess({
          payload: {
            content: candidates,
          },
        }),
        LayoutActions.layoutHeaderLoadJobsSuccess({
          payload: { content: jobs },
        }),
        LayoutActions.layoutHeaderLoadCompaniesSuccess({
          payload: { content: companies },
        }),
      ]),
    ),
  );

  constructor(
    private actions$: Actions,
    private candidateService: CandidateService,
    private jobService: JobService,
    private companyService: CompanyService,
    private store: Store<{}>,
  ) {}
}
